import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAdminSession } from "@/hooks/useAdminSession";
import {
  approveCertRequest,
  finalizeCertRequest,
  getCertRequest,
  markReady,
  rejectCertRequest,
  runTestsetid,
  syncCertRequest,
  syncTenant,
  type CertRequestDetailOrEmpty,
  type FinalizeCertRequestBody,
  type RunTestsetIdBody,
} from "./cert-requests";
import { companyQk } from "./companies.queries";

export const certRequestQk = {
  detail: (companyId: string) => ["admin", "cert-request", companyId] as const,
};

function shouldAutoRefresh(data: CertRequestDetailOrEmpty | undefined): number | false {
  if (!data || !("certRequest" in data) || !data.certRequest) return false;
  const inFlight = ["draft", "submitted", "verified"].includes(data.certRequest.status);
  return inFlight ? 15_000 : false;
}

export function useCertRequest(companyId: string | undefined) {
  const enabled = useAdminSession();
  return useQuery({
    queryKey: certRequestQk.detail(companyId ?? ""),
    enabled: enabled && !!companyId,
    queryFn: () => getCertRequest(companyId!),
    refetchInterval: (q) => shouldAutoRefresh(q.state.data),
    refetchIntervalInBackground: false,
  });
}

function useInvalidate(companyId: string | undefined) {
  const qc = useQueryClient();
  return () => {
    if (!companyId) return;
    qc.invalidateQueries({ queryKey: certRequestQk.detail(companyId) });
    qc.invalidateQueries({ queryKey: companyQk.detail(companyId) });
  };
}

export function useSyncCertRequest(companyId: string | undefined) {
  const invalidate = useInvalidate(companyId);
  return useMutation({
    mutationFn: () => syncCertRequest(companyId!),
    onSuccess: invalidate,
  });
}

export function useApproveCertRequest(companyId: string | undefined) {
  const invalidate = useInvalidate(companyId);
  return useMutation({
    mutationFn: () => approveCertRequest(companyId!),
    onSuccess: invalidate,
  });
}

export function useRejectCertRequest(companyId: string | undefined) {
  const invalidate = useInvalidate(companyId);
  return useMutation({
    mutationFn: (reason: string) => rejectCertRequest(companyId!, reason),
    onSuccess: invalidate,
  });
}

export function useFinalizeCertRequest(companyId: string | undefined) {
  const invalidate = useInvalidate(companyId);
  return useMutation({
    mutationFn: (body: FinalizeCertRequestBody) => finalizeCertRequest(companyId!, body),
    onSuccess: invalidate,
  });
}

export function useRunTestsetid(companyId: string | undefined) {
  const invalidate = useInvalidate(companyId);
  return useMutation({
    mutationFn: (body: RunTestsetIdBody) => runTestsetid(companyId!, body),
    onSuccess: invalidate,
  });
}

export function useSyncTenant(companyId: string | undefined) {
  const invalidate = useInvalidate(companyId);
  return useMutation({
    mutationFn: () => syncTenant(companyId!),
    onSuccess: invalidate,
  });
}

export function useMarkReady(companyId: string | undefined) {
  const invalidate = useInvalidate(companyId);
  return useMutation({
    mutationFn: () => markReady(companyId!),
    onSuccess: invalidate,
  });
}